
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Suspense, lazy } from "react";
import Sidebar from "./components/Sidebar";
import { Toaster } from "sonner";
import Loader from "./components/Loader";

// Lazy load all page components for better code splitting
const Landing = lazy(() => import("./pages/Landing"));
const TestLanding = lazy(() => import("./pages/TestLanding"));
const Testimonials = lazy(() => import("./pages/Testimonials"));
const PublicBookingPage = lazy(() => import("./pages/PublicBookingPage"));
const Login = lazy(() => import("./pages/Login"));
const Home = lazy(() => import("./pages/Home"));
const Scooters = lazy(() => import("./pages/Scooters"));
const Clients = lazy(() => import("./pages/Clients"));
const Contracts = lazy(() => import("./pages/Contracts"));
const BookingRequests = lazy(() => import("./pages/BookingRequests"));
const Users = lazy(() => import("./pages/Users"));
const Finance = lazy(() => import("./pages/Finance"));
const UserActions = lazy(() => import("./pages/UserActions"));
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const Management = lazy(() => import("./pages/Management"));
const BusinessDetails = lazy(() => import("./pages/BusinessDetails"));
const LandingAnalytics = lazy(() => import("./pages/LandingAnalytics"));

// Mobile components
const MobileLayout = lazy(() => import("./components/MobileLayout"));
const LoginMobile = lazy(() => import("./pages/mobile/LoginMobile"));
import MobileRedirect from "./components/MobileRedirect"; 
import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { clearAuth, refreshAccessToken, setAccessToken, getAccessToken, scheduleAccessTokenRefresh } from "@/lib/auth";
import { isMobileDevice } from "@/lib/mobile";
import Button from "@/components/Buttons";

function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const items = useMemo(() => ([
    { label: "Go to Home", action: () => (window.location.href = "/dashboard") },
    { label: "Go to Scooters", action: () => (window.location.href = "/scooters") },
    { label: "Go to Clients", action: () => (window.location.href = "/clients") },
    { label: "Go to Contracts", action: () => (window.location.href = "/contracts") },
    { label: "Open How It Works", action: () => (window.location.href = "/how-it-works") },
  ]), []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const filtered = items.filter(i => i.label.toLowerCase().includes(query.toLowerCase()));

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100]" onClick={() => setOpen(false)}>
      <div className="absolute inset-0 bg-black/40" />
      <div className="absolute left-1/2 top-20 -translate-x-1/2 w-full max-w-xl rounded-2xl border border-border-subtle bg-bg-elevated shadow-soft overflow-hidden"
        onClick={(e) => e.stopPropagation()}>
        <input
          autoFocus
          className="w-full px-4 py-3 bg-bg border-b border-border-subtle outline-none"
          placeholder="Type a command or search…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <div className="max-h-80 overflow-auto">
          {filtered.length === 0 ? (
            <div className="px-4 py-6 text-sm text-text-dim">No results</div>
          ) : (
            filtered.map((item, idx) => (
              <Button
                key={idx}
                variant="ghost"
                className="w-full text-left justify-start border-t border-border-subtle rounded-none"
                onClick={() => { item.action(); setOpen(false); }}
              >
                {item.label}
              </Button>
            ))
          )}
        </div>
        <div className="px-4 py-2 text-xs text-text-dim border-t border-border-subtle">Press Esc to close • Cmd/Ctrl+K</div>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Toaster 
        richColors 
        position="top-right" 
        expand={true}
        visibleToasts={5}
        toastOptions={{
          style: {
            marginBottom: '8px',
          }
        }}
      />
      <CommandPalette />
      <MobileRedirect />
      <Routes>
        {/* Mobile routes - check these first */}
        <Route path="/m/login" element={<MobileLoginGuard />} />
        <Route path="/m/*" element={<MobileLayout />} />
        
        {/* Desktop routes */}
        <Route path="/" element={<LandingGuard />} />
        <Route path="/testimonials" element={
          <Suspense fallback={
            <div className="flex min-h-screen items-center justify-center">
              <Loader />
            </div>
          }>
            <Testimonials />
          </Suspense>
        } />
        <Route path="/book/:orgSlug" element={
          <Suspense fallback={
            <div className="flex min-h-screen items-center justify-center">
              <Loader />
            </div>
          }>
            <PublicBookingPage />
          </Suspense>
        } />
        <Route path="/login" element={<LoginGuard />} />
        <Route path="/*" element={<Layout />} />
      </Routes>
    </BrowserRouter>
  );
}

function LandingGuard() {
  const [user, setUser] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function checkAuth() {
      // Quick check: if no token in memory and no refresh token cookie, skip auth check
      let token = getAccessToken();
      if (!token) {
        // Only try refresh if there might be a cookie (check if cookies are available)
        try {
          token = await refreshAccessToken() || null;
          if (token) { 
            setAccessToken(token); 
            scheduleAccessTokenRefresh(token); 
          } else {
            // No token and no refresh token - definitely not logged in, skip API call
            setUser(null);
            setLoading(false);
            return;
          }
        } catch (e) {
          // Refresh failed, definitely not logged in
          setUser(null);
          setLoading(false);
          return;
        }
      }
      
      // Only call /auth/me if we have a token
      try {
        const res = await fetch(`${import.meta.env.VITE_API_BASE}/auth/me`, { 
          headers: { Authorization: `Bearer ${token}` } 
        });
        if (res.ok) {
          const body = await res.json();
          setUser(body);
        } else {
          // Token invalid, try refresh once more
          const refreshed = await refreshAccessToken();
          if (refreshed) {
            setAccessToken(refreshed);
            scheduleAccessTokenRefresh(refreshed);
            const retry = await fetch(`${import.meta.env.VITE_API_BASE}/auth/me`, { headers: { Authorization: `Bearer ${refreshed}` } });
            if (retry.ok) {
              const body = await retry.json();
              setUser(body);
            } else {
              setUser(null);
            }
          } else {
            setUser(null);
          }
        }
      } catch (e) { 
        setUser(null); 
      }
      setLoading(false);
    }
    checkAuth();
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <div className="text-text-dim">Loading...</div>
        </div>
      </div>
    );
  }

  // Redirect to dashboard if authenticated
  if (user) {
    if (isMobileDevice()) {
      return <Navigate to="/m/contracts" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Suspense fallback={
      <div className="flex min-h-screen items-center justify-center">
        <Loader />
      </div>
    }>
      <TestLanding />
    </Suspense>
  );
}

function LoginGuard() {
  const [user, setUser] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function checkAuth() {
      let token = getAccessToken();
      if (!token) {
        token = await refreshAccessToken() || null;
        if (token) { setAccessToken(token); scheduleAccessTokenRefresh(token); }
      }
      try {
        if (!token) throw new Error('no token');
        const res = await fetch(`${import.meta.env.VITE_API_BASE}/auth/me`, { 
          headers: { Authorization: `Bearer ${token}` } 
        });
        if (res.ok) {
          const body = await res.json();
          setUser(body);
        } else {
          // attempt a one-time refresh if token invalid
          const refreshed = await refreshAccessToken();
          if (refreshed) {
            setAccessToken(refreshed);
            scheduleAccessTokenRefresh(refreshed);
            const retry = await fetch(`${import.meta.env.VITE_API_BASE}/auth/me`, { headers: { Authorization: `Bearer ${refreshed}` } });
            if (retry.ok) {
              const body = await retry.json();
              setUser(body);
            } else {
              setUser(null);
            }
          } else {
            setUser(null);
          }
        }
      } catch (e) { 
        setUser(null); 
      }
      setLoading(false);
    }
    checkAuth();
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <div className="text-text-dim">Loading...</div>
        </div>
      </div>
    );
  }

  // Redirect to home if already authenticated (desktop) or mobile home (mobile)
  if (user) {
    if (isMobileDevice()) {
      return <Navigate to="/m/contracts" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Suspense fallback={
      <div className="flex min-h-screen items-center justify-center">
        <Loader />
      </div>
    }>
      <Login />
    </Suspense>
  );
}



function MobileLoginGuard() {
  const [user, setUser] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function checkAuth() {
      let token = getAccessToken();
      if (!token) {
        token = await refreshAccessToken() || null;
        if (token) { setAccessToken(token); scheduleAccessTokenRefresh(token); }
      }
      try {
        if (!token) throw new Error('no token');
        const res = await fetch(`${import.meta.env.VITE_API_BASE}/auth/me`, { 
          headers: { Authorization: `Bearer ${token}` } 
        });
        if (res.ok) {
          const body = await res.json();
          setUser(body);
        } else {
          const refreshed = await refreshAccessToken();
          if (refreshed) {
            setAccessToken(refreshed);
            scheduleAccessTokenRefresh(refreshed);
            const retry = await fetch(`${import.meta.env.VITE_API_BASE}/auth/me`, { headers: { Authorization: `Bearer ${refreshed}` } });
            if (retry.ok) {
              const body = await retry.json();
              setUser(body);
            } else {
              setUser(null);
            }
          } else {
            setUser(null);
          }
        }
      } catch (e) { 
        setUser(null); 
      }
      setLoading(false);
    }
    checkAuth();
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-cyan mx-auto mb-4"></div>
          <div className="text-text-dim">Loading...</div>
        </div>
      </div>
    );
  }

  // Redirect to mobile home if already authenticated
  if (user) {
    return <Navigate to="/m/contracts" replace />;
  }

  return (
    <Suspense fallback={
      <div className="flex min-h-screen items-center justify-center bg-bg">
        <Loader />
      </div>
    }>
      <LoginMobile />
    </Suspense>
  );
}

function Layout() {
  const [user, setUser] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const qc = useQueryClient();

  useEffect(() => {
    async function fetchMe() {
      let token = getAccessToken();
      if (!token) {
        token = await refreshAccessToken() || null;
        if (token) { setAccessToken(token); scheduleAccessTokenRefresh(token); }
      }
      try {
        if (!token) throw new Error('no token');
        const res = await fetch(`${import.meta.env.VITE_API_BASE}/auth/me`, { 
          headers: { Authorization: `Bearer ${token}` } 
        });
        if (!res.ok) {
          const refreshed = await refreshAccessToken();
          if (refreshed) {
            setAccessToken(refreshed);
            scheduleAccessTokenRefresh(refreshed);
            const retry = await fetch(`${import.meta.env.VITE_API_BASE}/auth/me`, { headers: { Authorization: `Bearer ${refreshed}` } });
            if (retry.ok) {
              const body = await retry.json();
              setUser(body);
            } else {
              clearAuth();
              qc.clear();
              setUser(null);
            }
          } else {
            clearAuth();
            qc.clear();
            setUser(null);
          }
        } else {
          const body = await res.json();
          setUser(body);
        }
      } catch (e) { 
        clearAuth();
        qc.clear();
        setUser(null); 
      }
      setLoading(false);
    }
    fetchMe();
  }, [qc]);

  // Show loading while checking auth
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center" data-testid="auth-loading">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <div className="text-text-dim">Loading...</div>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    if (isMobileDevice()) {
      return <Navigate to="/m/login" replace />;
    }
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <main className="flex-1 bg-bg overflow-x-auto overflow-y-auto">
        <Suspense fallback={
          <div className="flex items-center justify-center min-h-screen" data-testid="app-main-loading">
            <Loader />
          </div>
        }>
          <Routes>
            <Route path="/dashboard" element={<Home />} />
            <Route path="/scooters" element={<Scooters />} />
            <Route path="/clients" element={<Clients />} />
            <Route path="/contracts" element={<Contracts />} />
            <Route path="/contracts/requests" element={<BookingRequests />} />
            <Route path="/finance" element={<Finance />} />
            <Route path="/users" element={<Users />} />
            <Route path="/user-actions" element={<UserActions />} />
            <Route path="/management" element={<Management />} />
            <Route path="/management/business-details" element={<BusinessDetails />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/analytics" element={<LandingAnalytics />} />
            <Route path="*" element={<div className="p-6">Not found</div>} />
          </Routes>
        </Suspense>
      </main>
    </div>
  );
}
